import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Logger from "../../../services/Logger";
import AdminWordWheelPackService from "../pack/AdminWordWheelPackService";
import AdminWordWheelService from "./AdminWordWheelService";
import { DRAFT } from "../board/AdminWordWheelBoardService";
import {
    SEASON_LEVEL_CATALOG_MIN,
    WORD_WHEEL_DEFAULT_SEASON,
    WORD_WHEEL_SEASON_OPTIONS,
    extractSeasonFromRow,
    formatSeasonComment,
    isJourneyPack,
} from "../wordWheelLevelUtils";

const NO_PACK = "none";

const FALLBACK_PACKS = [
    { code: "season_2026", name: "Season 2026", locked: true },
    { code: "classic", name: "Classic", locked: false },
    { code: "hard_quest", name: "Hard Quest", locked: false },
    { code: "master", name: "Master", locked: false },
];

function formatLanguageLabel(language) {
    if (!language || language === "all") {
        return "all languages";
    }
    return language;
}

function packOptions(items) {
    const source = Array.isArray(items) && items.length > 0 ? items : FALLBACK_PACKS;
    const byCode = new Map();
    for (const pack of source) {
        if (!pack?.code) {
            continue;
        }
        byCode.set(String(pack.code), {
            code: String(pack.code),
            name: pack.name || pack.code,
            locked: Boolean(pack.locked) || isJourneyPack(pack.code),
        });
    }
    for (const code of WORD_WHEEL_SEASON_OPTIONS) {
        if (!byCode.has(code)) {
            byCode.set(code, {
                code,
                name: formatSeasonComment(code) || code,
                locked: isJourneyPack(code),
            });
        }
    }
    return [...byCode.values()].sort((left, right) => {
        if (left.locked !== right.locked) {
            return left.locked ? -1 : 1;
        }
        return String(left.name).localeCompare(String(right.name));
    });
}

function sharedTemplatePack(templates) {
    if (!Array.isArray(templates) || templates.length === 0) {
        return NO_PACK;
    }
    const packs = templates.map((tpl) => extractSeasonFromRow(tpl)).map((value) => String(value || "").trim());
    const first = packs[0];
    if (!first) {
        return NO_PACK;
    }
    if (packs.every((value) => value === first)) {
        return first;
    }
    return NO_PACK;
}

export default function WordWheelPublishDialog({
    open,
    onClose,
    onConfirm,
    templates = [],
    language = "all",
    loading = false,
    allowPublishAllDraft = false,
}) {
    const [packs, setPacks] = useState(FALLBACK_PACKS);
    const [pack, setPack] = useState(NO_PACK);
    const [comment, setComment] = useState("");
    const [isFree, setIsFree] = useState(true);
    const [publishAllDraft, setPublishAllDraft] = useState(false);
    const [packCountLoading, setPackCountLoading] = useState(false);
    const [publishedInPack, setPublishedInPack] = useState(null);
    const [draftCountLoading, setDraftCountLoading] = useState(false);
    const [draftCount, setDraftCount] = useState(null);

    const options = useMemo(() => {
        const base = packOptions(packs);
        const current = sharedTemplatePack(templates);
        if (current !== NO_PACK && !base.some((item) => item.code === current)) {
            base.push({
                code: current,
                name: formatSeasonComment(current) || current,
                locked: isJourneyPack(current),
            });
            base.sort((left, right) => {
                if (left.locked !== right.locked) {
                    return left.locked ? -1 : 1;
                }
                return String(left.name).localeCompare(String(right.name));
            });
        }
        return base;
    }, [packs, templates]);
    const selectedPack = options.find((item) => item.code === pack);
    const inPack = Boolean(pack) && pack !== NO_PACK;
    const inJourney = isJourneyPack(pack);
    const packLabel = selectedPack?.name || formatSeasonComment(pack) || pack;

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        let cancelled = false;
        AdminWordWheelPackService.list()
            .then((items) => {
                if (!cancelled && Array.isArray(items) && items.length > 0) {
                    setPacks(items);
                }
            })
            .catch((error) => {
                Logger.error("Error loading packs for publish dialog:", error);
            });
        return () => {
            cancelled = true;
        };
    }, [open]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const initialPack = sharedTemplatePack(templates);
        setPack(initialPack);
        setComment(initialPack === NO_PACK ? "" : formatSeasonComment(initialPack));
        setIsFree(true);
        setPublishAllDraft(false);
        setDraftCount(null);
        setPublishedInPack(null);
    }, [open, templates]);

    useEffect(() => {
        if (!open || !inPack) {
            setPackCountLoading(false);
            setPublishedInPack(null);
            return undefined;
        }
        let cancelled = false;
        setPackCountLoading(true);
        AdminWordWheelService.countPublishedInSeason(pack, language)
            .then((count) => {
                if (!cancelled) {
                    setPublishedInPack(count);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPublishedInPack(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setPackCountLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open, pack, language, inPack]);

    useEffect(() => {
        if (!open || !allowPublishAllDraft) {
            return undefined;
        }
        let cancelled = false;
        setDraftCountLoading(true);
        AdminWordWheelService.countTemplatesByState(DRAFT, language)
            .then((count) => {
                if (!cancelled) {
                    setDraftCount(count);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDraftCount(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setDraftCountLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open, allowPublishAllDraft, language]);

    const handlePackChange = (nextPack) => {
        setPack(nextPack);
        setComment(nextPack === NO_PACK ? "" : formatSeasonComment(nextPack));
    };

    const handleConfirm = () => {
        if (!pack) {
            return;
        }
        onConfirm({
            season: inPack ? pack : "",
            comment: comment.trim(),
            isFree,
            publishAllDraft: allowPublishAllDraft && publishAllDraft,
        });
    };

    const countLabel = !inPack
        ? "No pack — published on its own"
        : packCountLoading
            ? "Checking pack…"
            : publishedInPack != null
                ? `${publishedInPack} published in ${packLabel}`
                : "Could not load pack count";

    const draftCountLabel = draftCountLoading
        ? "Checking DRAFT count…"
        : draftCount != null
            ? `${draftCount} DRAFT template${draftCount === 1 ? "" : "s"} in ${formatLanguageLabel(language)}`
            : "Could not load DRAFT count";

    const usingAllDraft = allowPublishAllDraft && publishAllDraft;
    const targetCount = usingAllDraft ? (draftCount ?? 0) : templates.length;
    const canPublish = usingAllDraft
        ? !draftCountLoading && (draftCount ?? 0) > 0
        : templates.length > 0;

    let publishSummary;
    if (usingAllDraft) {
        publishSummary = `Publishing all ${draftCount ?? 0} DRAFT template${draftCount === 1 ? "" : "s"} in ${formatLanguageLabel(language)}. Each will be reviewed and published with the settings below.`;
    } else if (templates.length === 1) {
        publishSummary = `Publishing "${templates[0]?.title || "Untitled"}".`;
    } else {
        publishSummary = `Publishing ${templates.length} selected template${templates.length === 1 ? "" : "s"}.`;
    }

    const selectValue = options.some((item) => item.code === pack) || pack === NO_PACK
        ? pack
        : NO_PACK;

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Publish Word Wheel Template{targetCount > 1 ? "s" : ""}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        {publishSummary}
                    </Typography>

                    {allowPublishAllDraft && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={publishAllDraft}
                                        onChange={(e) => setPublishAllDraft(e.target.checked)}
                                        disabled={loading || draftCountLoading}
                                    />
                                )}
                                label="Publish all DRAFT puzzles"
                            />
                            <Typography variant="caption" color="text.secondary">
                                {draftCountLabel}
                                {usingAllDraft && (draftCount ?? 0) > 0 && (
                                    <> · ignores current row selection</>
                                )}
                            </Typography>
                        </Box>
                    )}

                    <FormControl fullWidth size="small">
                        <InputLabel id="ww-publish-pack-label">Pack</InputLabel>
                        <Select
                            labelId="ww-publish-pack-label"
                            label="Pack"
                            value={selectValue}
                            onChange={(e) => handlePackChange(e.target.value)}
                            disabled={loading}
                        >
                            <MenuItem value={NO_PACK}>No pack</MenuItem>
                            {options.map((option) => (
                                <MenuItem key={option.code} value={option.code}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {inPack && selectValue === NO_PACK ? (
                        <Alert severity="warning">
                            This puzzle is in {formatSeasonComment(pack) || pack}. Choose that pack above, or pick another pack.
                        </Alert>
                    ) : null}

                    <TextField
                        label="Comment"
                        size="small"
                        fullWidth
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        disabled={loading}
                        placeholder={inPack ? packLabel : "Published"}
                    />

                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={isFree}
                                onChange={(e) => setIsFree(e.target.checked)}
                                disabled={loading}
                            />
                        )}
                        label="Free puzzle"
                    />

                    <Typography variant="caption" color="text.secondary">
                        {countLabel}
                        {inJourney && publishedInPack != null && !packCountLoading && (
                            <> · assign levels from Word Wheel Statistics after {SEASON_LEVEL_CATALOG_MIN} puzzles</>
                        )}
                    </Typography>

                    {inJourney ? (
                        <Alert severity="info">
                            This puzzle will be added to the {WORD_WHEEL_DEFAULT_SEASON} journey catalog. Puzzle levels are not set at
                            publish time. After {WORD_WHEEL_DEFAULT_SEASON} has at least {SEASON_LEVEL_CATALOG_MIN} published puzzles, use{" "}
                            <strong>Season stats + levels</strong> on the Word Wheel Statistics page to calculate and
                            assign levels for the whole season.
                        </Alert>
                    ) : inPack ? (
                        <Alert severity="info">
                            This puzzle will be published in the {packLabel} pack
                            {selectedPack?.locked ? " (view-only journey pack)." : "."}
                        </Alert>
                    ) : (
                        <Alert severity="info">
                            This puzzle will be published on its own. It will not appear in a pack.
                            It can still be used as a daily puzzle from the no-pack catalog.
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirm}
                    disabled={loading || !pack || (inPack && packCountLoading) || !canPublish}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    Publish{targetCount > 0 ? ` (${targetCount})` : ""}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
